import React from 'react';
import { Users, Check, ExternalLink, Heart, Award } from 'lucide-react';
import { membershipInfo } from '../data/mock';

const MembershipPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '50vh' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2f23] to-[#2a5a3d]" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6">Become a Member</h1>
            <p className="text-white/70 text-lg">
              Join our community and be part of a thriving Shi'a Muslim congregation.
            </p>
            <div className="islamic-divider mt-8">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* Membership Info Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="card card-gold text-center">
              <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                <Award className="w-10 h-10 text-[#c5a572]" />
              </div>
              
              <h2 className="text-3xl text-[#1a2f23] mb-6">Associated Member Applications</h2>
              
              <p className="text-[#5a6d62] text-lg leading-relaxed mb-10">
                {membershipInfo.description}
              </p>
              
              <a 
                href={membershipInfo.registrationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-3 text-lg"
              >
                <ExternalLink className="w-5 h-5" />
                Apply for Membership
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-alt pattern-overlay">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Why Join</p>
            <h2 className="heading-2 mb-6">Membership Benefits</h2>
            <div className="islamic-divider">
              <div className="islamic-star" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {membershipInfo.benefits.map((benefit, index) => (
              <div key={`benefit-${benefit.slice(0,20)}`} className="card group">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 border border-[#c5a572]/30 group-hover:border-[#c5a572] group-hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300">
                    <Check className="w-5 h-5 text-[#c5a572]" />
                  </div>
                  <p className="text-[#1a2f23] group-hover:text-[#c5a572] transition-colors">{benefit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '300px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h2 className="text-4xl text-white mb-8">Ready to Join Our Community?</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              We welcome all families and individuals who wish to be part of our community. 
              Your membership supports our programs and services, and gives you a voice in 
              shaping the future of Fatimia Community Services Society.
            </p>
            <a 
              href={membershipInfo.registrationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-white inline-flex items-center gap-3"
            >
              <Heart className="w-5 h-5" />
              Register as Associated Member
            </a>
            <div className="islamic-divider mt-12">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Questions</p>
              <h2 className="heading-2">Frequently Asked</h2>
            </div>
            
            <div className="space-y-6">
              {[
                {
                  q: 'How do I become an Associated Member?',
                  a: 'Simply click on the "Apply for Membership" button above to fill out our online registration form. Our team will review your application and contact you.'
                },
                {
                  q: 'Who can apply for membership?',
                  a: 'We welcome all families and individuals who wish to be part of our community and support our mission.'
                },
                {
                  q: 'What are the membership fees?',
                  a: 'Membership fee details are provided in the registration form. We believe everyone should be part of our community, so please contact us if you need fee assistance.'
                },
                {
                  q: 'Do I need to be a member to attend programs?',
                  a: 'No, everyone is welcome to attend our programs. Membership is not required to participate in our events and activities.'
                }
              ].map((faq, index) => (
                <div key={`faq-${faq.q.slice(0,20)}`} className="card gold-border-left">
                  <h3 className="text-lg text-[#1a2f23] mb-3">{faq.q}</h3>
                  <p className="text-[#5a6d62]">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembershipPage;
